/*

  Usage:

  const Page = () => {
    const handler = useCallback(() => {
      ....
    }, [])

    const removeScrollListener = useEventListener('scroll', handler)

    return (
      <Fragment>
        <div>
          <Text />
          <Text />
        <div>
        <button ref={buttonRef}>Click me</button>
      </Fragment>
    )
  }

  The hook is used to install the handler on the event,
  inside the hook we automatically install a subscription to the did mount and an unsubscribe from the event

  The hook also returns a method that allows you to unsubscribe from a event

 */

import { useEffect, useRef, useCallback } from 'react'


const useEventListener = (eventName, handler, element = window) => {
  const handlerRef = useRef(null)

  useEffect(() => {
    handlerRef.current = handler
  }, [ handler ])

  const eventListener = useCallback((event) => {
    handlerRef.current(event)
  }, [])

  const removeListener = useCallback(() => {
    element.removeEventListener(eventName, eventListener)
  }, [ eventName, element, eventListener ])

  useEffect(() => {
    if (element && element.addEventListener) {
      element.addEventListener(eventName, eventListener)

      return () => {
        element.removeEventListener(eventName, eventListener)
      }
    }
  }, [ eventName, element, eventListener ])

  return removeListener
}


export default useEventListener
